import classnames from 'classnames'
import { css } from 'emotion'
import {
  LoanSortOption,
  LoanSortOptionValues,
  LoanSortOptionWithAmountValues,
  LoanSortOptionWithClosestAmountValues,
  LoanSortOptionWithConversionProbabilityValues,
  LoanStep,
  Product,
  RedirectType,
  SortDirection,
} from 'even-ts-static'
import * as React from 'react'

import SortOptions, { SortStyles } from 'embed/components/SortOptions'
import { TRANSUNION_COMPANY_UUID } from 'embed/constants'
import { LoanOffer } from 'embed/constants/models'
import { AprVsRecSorting, SortByClosestLoanAmount } from 'embed/experiments'
import AdvertiserDisclosure from '../AdvertiserDisclosure'
import AprTypeToggleFilter from '../AprTypeToggleFilter'
import { NoPreviewOffersMessage } from '../NoPreviewOffersMessage'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import ListItem from 'ui/multi-product/components/Loan/ListItem'

const resultsStyles = require('embed/styles/results')

export interface Props {
  allLoanOffersByLender: { [s: string]: LoanOffer[] }
  bestOfferId?: string
  companyUuid: string
  currentStep: string
  dispatchOfferCta: (loan: LoanOffer, redirectType?: RedirectType) => void
  displaySortBarItems?: boolean
  isDotDash: boolean
  offerListRef: React.Ref<HTMLOListElement>
  onSeeMoreFromLenderClick: (offer: LoanOffer) => void
  onSortOptionClick: (sortOption: LoanSortOption) => void
  selectedAprVsRecSortingVariantId?: string
  selectedSortByClosestLoanAmountVariantId?: string
  showDefaultSortOption?: boolean
  showSeeMoreOffers?: boolean
  sort: LoanSortOption
  sortDirection: SortDirection
  loanOffers: LoanOffer[]
  customSortStyles?: SortStyles
  viewportHeight?: number
  viewportWidth?: number
  isResultsView: boolean
}

// eslint-disable-next-line complexity
export const LoanOfferList: React.FC<Props> = ({
  companyUuid,
  allLoanOffersByLender,
  bestOfferId,
  currentStep,
  customSortStyles,
  dispatchOfferCta,
  displaySortBarItems,
  isDotDash,
  isResultsView,
  loanOffers,
  offerListRef,
  onSeeMoreFromLenderClick,
  onSortOptionClick,
  selectedAprVsRecSortingVariantId,
  selectedSortByClosestLoanAmountVariantId,
  showDefaultSortOption,
  showSeeMoreOffers,
  sort,
  sortDirection,
  viewportHeight,
  viewportWidth,
}) => {
  const isTransunionSupply = companyUuid === TRANSUNION_COMPANY_UUID
  const shouldDisplayTransunionDisclaimer = isTransunionSupply && isResultsView
  const transunionDisclaimer = (
    <ThemeConsumer>
      {({ colorTextSecondary }) => (
        <span
          className={classnames(
            resultsStyles.notFirmOffers,
            css`
              color: ${colorTextSecondary};
            `,
          )}
        >
          These are not firm offers of credit |
        </span>
      )}
    </ThemeConsumer>
  )

  let sortOptionValues
  if (selectedAprVsRecSortingVariantId === AprVsRecSorting.variants.ConversionProbability.id) {
    sortOptionValues = LoanSortOptionWithConversionProbabilityValues
  } else if (currentStep === LoanStep.OfferList) {
    sortOptionValues = LoanSortOptionValues
  } else if (
    isResultsView &&
    selectedSortByClosestLoanAmountVariantId === SortByClosestLoanAmount.variants.SortByClosestAmount.id
  ) {
    sortOptionValues = LoanSortOptionWithClosestAmountValues
  } else {
    sortOptionValues = LoanSortOptionWithAmountValues
  }

  return (
    <div>
      <NoPreviewOffersMessage offers={loanOffers} product={Product.Loan} />
      {loanOffers.length > 0 && (
        <div className={resultsStyles.sortBarContainer}>
          <div className={resultsStyles.sortFilterContainer}>
            <SortOptions
              customStyles={customSortStyles}
              sortOptionValues={sortOptionValues}
              sort={sort}
              sortDirection={sortDirection}
              onSortOptionClick={onSortOptionClick}
              showDefaultSortOption={showDefaultSortOption}
            />
            {displaySortBarItems && viewportWidth && viewportWidth > 500 && (
              <AprTypeToggleFilter styles={{ toggleFilterListContainer: resultsStyles.sortBarAprTypeFilter }} />
            )}
          </div>
          {displaySortBarItems && (
            <div>
              {shouldDisplayTransunionDisclaimer && transunionDisclaimer}
              <AdvertiserDisclosure
                className={classnames(resultsStyles.sortBarDisclosure, {
                  [css`
                    position: absolute;
                  `]: isDotDash,
                })}
              />
            </div>
          )}
        </div>
      )}
      {loanOffers.length > 0 && viewportWidth && viewportWidth <= 500 && (
        <div className={resultsStyles.list}>
          {displaySortBarItems && (
            <AprTypeToggleFilter styles={{ toggleFilterListContainer: resultsStyles.emptyResults }} />
          )}
        </div>
      )}
      <ol ref={offerListRef} className={resultsStyles.list}>
        {loanOffers.map((offer, index) => {
          const lenderOffers = allLoanOffersByLender[offer.originator?.companyUuid ?? '']
          const hasMoreOffers = lenderOffers ? lenderOffers.length > 1 : false
          return (
            <ListItem
              bestOfferId={bestOfferId}
              dispatchOfferCta={dispatchOfferCta}
              dispatchSeeMoreFromLender={onSeeMoreFromLenderClick}
              hasMoreOffers={hasMoreOffers}
              isResultsView={isResultsView}
              key={`${index}-${offer.uuid}`}
              offer={offer}
              shouldDisplayTransunionDisclaimer={!!shouldDisplayTransunionDisclaimer}
              showSeeMoreOffers={showSeeMoreOffers}
              viewportHeight={viewportHeight}
              viewportWidth={viewportWidth}
            />
          )
        })}
      </ol>
    </div>
  )
}
